import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

interface LocaleData {
  language: string;
  data: any;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class IndexedStorageService {
  private readonly DB_NAME = 'LocaleDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'locales';
  private readonly CACHE_DURATION = 30 * 24 * 3600000; // 30 days

  constructor(private http: HttpClient) {}

  /**
   * Initialize IndexedDB
   */
  private initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'language' });
        }
      };
    });
  }

  /**
   * Save locale data to IndexedDB
   */
  private saveToIndexedDB(language: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.initDB().then(db => {
        const transaction = db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        
        const localeData: LocaleData = {
          language,
          data,
          timestamp: Date.now()
        };

        const request = store.put(localeData);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }).catch(reject);
    });
  }

  /**
   * Get locale data from IndexedDB
   */
  private getFromIndexedDB(language: string): Promise<LocaleData | null> {
    return new Promise((resolve, reject) => {
      this.initDB().then(db => {
        const transaction = db.transaction([this.STORE_NAME], 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        
        const request = store.get(language);
        request.onsuccess = () => {
          const result = request.result;
          if (result && this.isCacheValid(result.timestamp)) {
            resolve(result);
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      }).catch(reject);
    });
  }

  /**
   * Check if cache is valid
   */
  private isCacheValid(timestamp: number): boolean {
    return (Date.now() - timestamp) < this.CACHE_DURATION;
  }

  /**
   * Load locale data from server
   */
  private loadFromServer(language: string): Observable<any> {
    return this.http.get(`assets/locale/${language}.json`);
  }

  /**
   * Get locale data
   */
  getLocaleData(language: string): Observable<any> {
    return from(this.getFromIndexedDB(language)).pipe(
      switchMap(cachedData => {
        if (cachedData) {
          return of(cachedData.data);
        }

        return this.loadFromServer(language).pipe(
          tap(data => {
            // Save to IndexedDB after successful load
            this.saveToIndexedDB(language, data).catch(error => {
              console.warn('Failed to save locale to IndexedDB:', error);
            });
          }),
          catchError(error => {
            console.error(`Failed to load locale ${language}:`, error);
            return throwError(error);
          })
        );
      })
    );
  }

  /**
   * Get language list with cache strategy
   */
  getLanguagesData(): Observable<any> {
    return from(this.getFromIndexedDB('languages')).pipe(
      switchMap(cachedData => {
        if (cachedData) {
          return of(cachedData.data);
        }

        return this.http.get('assets/locale/languages.json').pipe(
          tap(data => {
            // Save to IndexedDB after successful load
            this.saveToIndexedDB('languages', data).catch(error => {
              console.warn('Failed to save languages to IndexedDB:', error);
            });
          }),
          catchError(error => {
            console.error('Failed to load languages:', error);
            return throwError(error);
          })
        );
      })
    );
  }

  /**
   * Clear current language cache
   */
  clearCache(language: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.initDB().then(db => {
        const transaction = db.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        
        const request = store.delete(language);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }).catch(reject);
    });
  }

  /**
   * Force refresh a locale: clear cache, load from server, then save to IndexedDB
   */
  refreshLocale(language: string): Observable<any> {
    return from(this.clearCache(language)).pipe(
      switchMap(() => this.loadFromServer(language)),
      tap(data => {
        this.saveToIndexedDB(language, data).catch(error => {
          console.warn('Failed to save refreshed locale to IndexedDB:', error);
        });
      })
    );
  }
  
}
